import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useState, type ReactNode } from "react";

import { MotionEasing } from "@/blueprint/tokens/Motion";
import { cn } from "@/lib/utils";

type CardGeometry = {
  x: string;
  rotate: number;
  scale: number;
  opacity: number;
};

/**
 * Resting geometry by depth, 0 being the active card at the front.
 *
 * This is Swiper's `effect="cards"`, which fans a pile sideways and only
 * sideways: each card behind sits a little further along, a little smaller and
 * a touch rotated, so the stack reads as cards squared off on a table. Nothing
 * is offset vertically, and nothing overshoots — the previous build of this
 * drifted the cards down and to the right, which read as a dropped pile.
 */
const RESTING: readonly CardGeometry[] = [
  { x: "0%", rotate: 0, scale: 1, opacity: 1 },
  { x: "6%", rotate: 2, scale: 0.96, opacity: 0.85 },
  { x: "12%", rotate: 3, scale: 0.92, opacity: 0.7 },
];

/** The active card plus the two peeking out behind it — one per resting
 *  geometry, so `restingAt` is in range for every depth by construction. */
const STACK_DEPTH = RESTING.length;

/**
 * Where a card arriving at the front comes in from: the mirror of where a
 * leaving one goes, a single step to the other side. A share of the card's
 * own width rather than a length, so the gesture is the same at 176px and at
 * 408px.
 */
const ARRIVING_AT_FRONT = { x: "7%", rotate: 3, scale: 1, opacity: 0 };

/**
 * Where a card leaving the front goes: barely anywhere. It shifts a single
 * step to the other side — the mirror of where `behind1` already rests — and
 * is gone before it gets there, so it reads as peeking out from behind the
 * card that has replaced it rather than sliding away off the pile. Its
 * rotation is the fan's own 3 degrees for the same reason: over 7% of travel,
 * the 6 degrees a full sweep carries would read as a twist in place.
 */
const LEAVING_FRONT = { x: "-7%", rotate: -3, scale: 1, opacity: 0 };

/** A card joining or leaving the back, one step deeper than the last one. */
const BEHIND_THE_PILE: CardGeometry = {
  x: "18%",
  rotate: 4,
  scale: 0.88,
  opacity: 0,
};

const DURATION_S = 0.35;
const TRANSITION = { duration: DURATION_S, ease: MotionEasing.outStrong };

/**
 * Leaving the front, the card is gone well inside the first half: 140ms of
 * the 350ms, so what little travel it has is nearly over by the time it
 * disappears. The transform still runs the full duration on the shared curve.
 */
const FRONT_FADE_S = 0.14;
const LEAVING_FRONT_TRANSITION = {
  ...TRANSITION,
  opacity: { duration: FRONT_FADE_S, ease: MotionEasing.outStrong },
};

/**
 * The same fade, run the other way: a card joining the stack is up to full
 * opacity inside the first half rather than dissolving in over the whole
 * step, which is what makes both directions read as a card appearing at the
 * edge of the pile rather than fading through it.
 */
const ARRIVING_TRANSITION = LEAVING_FRONT_TRANSITION;

/**
 * Under `prefers-reduced-motion` the stack crossfades in place: the geometry
 * is still per depth, but a card changing depth snaps to the new one and only
 * its opacity is animated.
 */
const REDUCED_TRANSITION = {
  opacity: { duration: DURATION_S, ease: "linear" as const },
  x: { duration: 0 },
  rotate: { duration: 0 },
  scale: { duration: 0 },
};

/** In range by construction — depth only ever runs 0..STACK_DEPTH - 1. */
const restingAt = (depth: number): CardGeometry => RESTING[depth]!;

type CardCustom = { direction: number; reduced: boolean };

const CARD_VARIANTS = {
  enter: ({ direction, reduced }: CardCustom) => {
    // Forward, the new card joins the back of the pile; back, it arrives at
    // the front from the side, which is the forward sweep run in reverse.
    const joiningTheBack = direction >= 0;
    if (reduced) {
      return {
        ...restingAt(joiningTheBack ? STACK_DEPTH - 1 : 0),
        opacity: 0,
      };
    }
    return joiningTheBack ? BEHIND_THE_PILE : ARRIVING_AT_FRONT;
  },
  exit: ({ direction, reduced }: CardCustom) => {
    const leavingTheFront = direction >= 0;
    if (reduced) {
      return {
        ...restingAt(leavingTheFront ? 0 : STACK_DEPTH - 1),
        opacity: 0,
        transition: REDUCED_TRANSITION,
      };
    }
    return leavingTheFront
      ? {
          ...LEAVING_FRONT,
          // Under the card replacing it, so the sliver that shows reads as
          // the old photo behind the new one rather than a ghost over it.
          zIndex: 0,
          transition: LEAVING_FRONT_TRANSITION,
        }
      : { ...BEHIND_THE_PILE, transition: TRANSITION };
  },
};

/**
 * Drives a [CardStack]. The index is deliberately unbounded rather than taken
 * modulo the item count: it is what keys the cards, and a card that stays in
 * the stack while changing depth has to keep its key or it would remount
 * instead of animating. Wrapping the index would also collide the card leaving
 * the front with the one joining the back whenever the stack is as deep as the
 * list is long, which is exactly the careers stack's three photos.
 */
export const useCardStack = () => {
  const [{ index, direction }, setState] = useState({
    index: 0,
    direction: 1,
  });
  const step = (delta: number) =>
    setState((current) => ({
      index: current.index + delta,
      direction: delta,
    }));

  return {
    index,
    direction,
    next: () => step(1),
    previous: () => step(-1),
  };
};

type CardStackProps = {
  /** How many items the stack cycles through; the front three are drawn. */
  count: number;
  /** From [useCardStack]. Unbounded — the stack wraps it itself. */
  index: number;
  /** From [useCardStack]. Which way the last step went. */
  direction: number;
  /** The whole stack is the control that advances it. */
  onAdvance: () => void;
  /** Accessible name for that control. */
  advanceLabel: string;
  /** Draws one item. `isActive` marks the front card, the only one named. */
  renderCard: (itemIndex: number, isActive: boolean) => ReactNode;
  /** Sizes the stack box; the cards fill it. */
  className?: string;
};

/**
 * A pile of cards that shuffles sideways, shared by the social photos on the
 * shop page and the founders photos on the home page — two hand-rolled stacks
 * with the same three depths and the same step logic before this.
 *
 * The cards behind deliberately overhang the box rather than being clipped by
 * it, which is what makes them read as a pile; nothing here sets `overflow`.
 */
const CardStack = ({
  count,
  index,
  direction,
  onAdvance,
  advanceLabel,
  renderCard,
  className,
}: CardStackProps) => {
  const reduced = useReducedMotion() === true;
  const custom = { direction, reduced };

  return (
    <button
      type="button"
      aria-label={advanceLabel}
      onClick={onAdvance}
      /* The cards are `absolute inset-0`, so the box they fill has to be the
         positioned one; a caller should not have to know that. */
      className={cn("relative", className)}
    >
      <AnimatePresence initial={false} custom={custom}>
        {Array.from({ length: STACK_DEPTH }, (_, depth) => {
          // The index is unbounded, so bring it back into range both ways.
          const itemIndex = (((index + depth) % count) + count) % count;
          // Forward the new card joins the back, backward it arrives at the
          // front, so which depth is entering follows from the direction.
          const isArriving =
            direction >= 0 ? depth === STACK_DEPTH - 1 : depth === 0;

          return (
            <motion.div
              key={index + depth}
              custom={custom}
              variants={CARD_VARIANTS}
              initial="enter"
              animate={restingAt(depth)}
              exit="exit"
              transition={
                reduced
                  ? REDUCED_TRANSITION
                  : isArriving
                    ? ARRIVING_TRANSITION
                    : TRANSITION
              }
              // The front card is on top, and a card sweeping off the front
              // keeps that, so it passes over the pile rather than under it.
              style={{ zIndex: STACK_DEPTH - depth }}
              className="absolute inset-0 overflow-hidden rounded-s d:rounded-m"
            >
              {renderCard(itemIndex, depth === 0)}
            </motion.div>
          );
        })}
      </AnimatePresence>
    </button>
  );
};

export default CardStack;
